import * as React from "react"
import { cn } from "@/lib/utils"
import { Badge } from "@/components/ui/badge"

// Navigation offset classes for when #primary-navigation is present
// Navigation height is h-14 (56px)
const navOffsetClasses = {
  small: [
    "[body:has(#primary-navigation)_&]:pt-[calc(56px+2rem)]",
    "md:[body:has(#primary-navigation)_&]:pt-[calc(56px+3rem)]",
    "lg:[body:has(#primary-navigation)_&]:pt-[calc(56px+4rem)]",
  ],
  medium: [
    "[body:has(#primary-navigation)_&]:pt-[calc(56px+3rem)]",
    "md:[body:has(#primary-navigation)_&]:pt-[calc(56px+5rem)]",
    "lg:[body:has(#primary-navigation)_&]:pt-[calc(56px+6rem)]",
  ],
  large: [
    "[body:has(#primary-navigation)_&]:pt-[calc(56px+4rem)]",
    "md:[body:has(#primary-navigation)_&]:pt-[calc(56px+6rem)]",
    "lg:[body:has(#primary-navigation)_&]:pt-[calc(56px+8rem)]",
  ],
}

const sizeClasses = {
  small: "py-8 md:py-12 lg:py-16",
  medium: "py-12 md:py-20 lg:py-24",
  large: "py-16 md:py-24 lg:py-32",
}

/**
 * Page header component matching the prosci-ui design system.
 *
 * Props:
 *   title        - Main heading text
 *   description  - Subheading / body text below the title
 *   badge        - Short label displayed above the title
 *   breadcrumb   - React node rendered above the title row
 *   headerImage  - URL of a background image with gradient overlay
 *   size         - "small" | "medium" (default) | "large"
 *   stacked      - Stack title and children vertically (default false)
 *   overlap      - Overlap with the content below (default false)
 *   titleAs      - HTML tag for the title: "h1" (default) | "h2" | "h3" | "h4"
 *   children     - Extra content rendered alongside the title
 *   className    - Additional classes for the <header> element
 */
const Header = React.forwardRef(
  (
    {
      className,
      title,
      description,
      badge,
      breadcrumb,
      headerImage,
      size = "medium",
      stacked = false,
      overlap = false,
      titleAs: TitleTag = "h1",
      children,
      ...props
    },
    ref
  ) => {
    return (
      <header
        ref={ref}
        className={cn(
          "page-header relative bg-primary rounded-br-[50px] overflow-hidden",
          sizeClasses[size],
          navOffsetClasses[size],
          overlap && "mb-[-50px]",
          className
        )}
        {...props}
      >
        {/* Inner content container */}
        <div
          className={cn(
            "relative z-10 container mx-auto flex flex-col md:mt-0 gap-4 items-center px-4 md:px-6",
            breadcrumb && "md:flex-col",
            overlap && "mb-[50px]"
          )}
        >
          {breadcrumb && (
            <div className="[&_a]:text-white/60 [&_a:hover]:text-white [&_span]:text-white/70 [&_svg]:text-white/40 w-full">
              {breadcrumb}
            </div>
          )}

          <div
            className={cn(
              "flex flex-col gap-4 md:gap-3 w-full",
              stacked
                ? "md:flex-col text-center items-center"
                : "md:flex-row md:justify-between"
            )}
          >
            {(badge || title || description) && (
              <div className="flex flex-col gap-2 md:gap-3 w-full">
                {badge && (
                  <Badge
                    variant="secondary"
                    className="bg-white text-primary uppercase text-xs font-semibold tracking-wide mx-auto rounded-tl-[0px] rounded-tr-[10px] rounded-br-[0px] rounded-bl-[10px]"
                  >
                    {badge}
                  </Badge>
                )}

                {title && (
                  <TitleTag
                    className={cn(
                      "block w-full text-white text-3xl md:text-4xl text-balance font-serif font-normal",
                      stacked ? "md:mb-[16px]" : "md:w-[calc(100%-165px)] lg:mb-0"
                    )}
                  >
                    {title}
                  </TitleTag>
                )}

                {description && (
                  <p
                    className={cn(
                      "block w-full text-white text-md text-balance max-w-3xl",
                      stacked ? "mx-auto" : ""
                    )}
                  >
                    {description}
                  </p>
                )}
              </div>
            )}

            {children}
          </div>
        </div>

        {/* Background image overlay */}
        {headerImage && (
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${headerImage})` }}
          >
            <div className="absolute inset-0 bg-gradient-to-b from-primary/70 from-10% via-primary/80 via-40% to-primary/95" />
          </div>
        )}
      </header>
    )
  }
)

Header.displayName = "Header"

export { Header }
